// frontend/src/components/shop/Checkout.js
import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, Loader, Home, User } from 'lucide-react';
import { shopAPI } from '../../api/shop';
import toast from 'react-hot-toast';

const HOME_DELIVERY_PRICE = 30;

const Checkout = ({ cart: propCart, onBack, onSuccess }) => {
  const [cart, setCart] = useState(propCart);
  const [notes, setNotes] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('coach');
  const [deliveryContactName, setDeliveryContactName] = useState('');
  const [deliveryContactPhone, setDeliveryContactPhone] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryPostalCode, setDeliveryPostalCode] = useState('');
  const [paymentAgreed, setPaymentAgreed] = useState(false);
  const [dataProcessingAgreed, setDataProcessingAgreed] = useState(false);
  const [coachAgreementAgreed, setCoachAgreementAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [isLoadingCart, setIsLoadingCart] = useState(false);
  const cartSubtotal = Number(cart?.total_price || 0);
  const deliveryPrice = deliveryMethod === 'home' ? HOME_DELIVERY_PRICE : 0;
  const orderTotal = cartSubtotal + deliveryPrice;

  // Загружаем корзину с сервера, если не передана
  useEffect(() => {
    if (!propCart || !propCart.items || propCart.items.length === 0) {
      setIsLoadingCart(true);
      shopAPI.getCart()
        .then((serverCart) => {
          setCart(serverCart);
        })
        .catch((error) => {
          console.error('Error loading cart:', error);
          toast.error('Ошибка загрузки корзины');
        })
        .finally(() => {
          setIsLoadingCart(false);
        });
    }
  }, [propCart]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Проверка всех согласий
    if (!paymentAgreed || !dataProcessingAgreed || !coachAgreementAgreed) {
      toast.error('Необходимо подтвердить все согласия');
      return;
    }

    if (
      deliveryMethod === 'home'
      && (
        !deliveryContactName.trim()
        || !deliveryContactPhone.trim()
        || !deliveryCity.trim()
        || !deliveryAddress.trim()
      )
    ) {
      toast.error('Заполните обязательные данные для доставки');
      return;
    }

    setIsSubmitting(true);

    try {
      await shopAPI.createOrder({
        notes,
        delivery_method: deliveryMethod,
        delivery_contact_name: deliveryContactName.trim(),
        delivery_contact_phone: deliveryContactPhone.trim(),
        delivery_city: deliveryCity.trim(),
        delivery_address: deliveryAddress.trim(),
        delivery_postal_code: deliveryPostalCode.trim(),
        payment_agreed: paymentAgreed,
        data_processing_agreed: dataProcessingAgreed,
        coach_agreement_agreed: coachAgreementAgreed,
      });
      setOrderCreated(true);
      toast.success('Заказ успешно оформлен!');
      
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      console.error('Error creating order:', error);
      const errorMessage = error.response?.data?.error || 'Ошибка оформления заказа';
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  if (isLoadingCart) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-12">
          <Loader className="w-8 h-8 animate-spin text-[#4897d2]" />
        </div>
      </div>
    );
  }

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Оформление заказа</h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-12">
          <div className="text-center">
            <p className="text-gray-600 text-lg">Корзина пуста</p>
          </div>
        </div>
      </div>
    );
  }

  if (orderCreated) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-12">
          <div className="text-center">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Заказ оформлен!
            </h2>
            <p className="text-gray-600 mb-4">
              Ваш заказ успешно создан. Мы свяжемся с вами в ближайшее время.
            </p>
            <p className="text-sm text-gray-500">
              Уведомление отправлено администратору
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900">Оформление заказа</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Order Summary */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Ваш заказ:</h3>
          <div className="space-y-3 mb-4">
            {cart.items.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">
                  {item.product.name} x{item.quantity}
                </span>
                <span className="font-medium text-gray-900">{item.total_price}₪</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <span className="text-lg font-semibold text-gray-900">Товары:</span>
            <span className="text-2xl font-bold text-[#4897d2]">
              {cart.total_price}₪
            </span>
          </div>
          {deliveryMethod === 'home' && (
            <div className="flex items-center justify-between pt-3">
              <span className="text-sm font-medium text-gray-700">Доставка на дом:</span>
              <span className="font-semibold text-gray-900">{deliveryPrice.toFixed(2)}₪</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-200">
            <span className="text-lg font-semibold text-gray-900">Итого:</span>
            <span className="text-2xl font-bold text-[#4897d2]">
              {orderTotal.toFixed(2)}₪
            </span>
          </div>
        </div>

        {/* Delivery Method */}
        <div className="mb-6">
          <label className="block mb-3 text-sm font-medium text-gray-700">
            Способ получения заказа:
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setDeliveryMethod('home')}
              className={`p-4 border-2 rounded-lg transition-all ${
                deliveryMethod === 'home'
                  ? 'border-[#4897d2] bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Home className={`w-6 h-6 mx-auto mb-2 ${
                deliveryMethod === 'home' ? 'text-[#4897d2]' : 'text-gray-400'
              }`} />
              <div className={`text-sm font-medium ${
                deliveryMethod === 'home' ? 'text-[#4897d2]' : 'text-gray-700'
              }`}>
                Доставка на дом
                <div className="mt-1 text-xs font-semibold text-gray-500">+{HOME_DELIVERY_PRICE.toFixed(2)}₪</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setDeliveryMethod('coach')}
              className={`p-4 border-2 rounded-lg transition-all ${
                deliveryMethod === 'coach'
                  ? 'border-[#4897d2] bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <User className={`w-6 h-6 mx-auto mb-2 ${
                deliveryMethod === 'coach' ? 'text-[#4897d2]' : 'text-gray-400'
              }`} />
              <div className={`text-sm font-medium ${
                deliveryMethod === 'coach' ? 'text-[#4897d2]' : 'text-gray-700'
              }`}>
                У тренера при встрече
              </div>
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Данные для доставки можно заполнять на английском или иврите.
          </p>
        </div>

        {deliveryMethod === 'home' && (
          <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50/60 p-4">
            <h4 className="mb-3 font-semibold text-gray-900">Данные для доставки</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-gray-700">
                Получатель
                <input
                  type="text"
                  value={deliveryContactName}
                  onChange={(e) => setDeliveryContactName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-[#4897d2]"
                  placeholder="Имя и фамилия"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Телефон
                <input
                  type="text"
                  value={deliveryContactPhone}
                  onChange={(e) => setDeliveryContactPhone(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-[#4897d2]"
                  placeholder="+972..."
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Город
                <input
                  type="text"
                  value={deliveryCity}
                  onChange={(e) => setDeliveryCity(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-[#4897d2]"
                  placeholder="Tel Aviv / תל אביב"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Индекс
                <input
                  type="text"
                  value={deliveryPostalCode}
                  onChange={(e) => setDeliveryPostalCode(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-[#4897d2]"
                  placeholder="Необязательно"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700 sm:col-span-2">
                Адрес
                <input
                  type="text"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-[#4897d2]"
                  placeholder="Улица, дом, квартира"
                />
              </label>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Примечания к заказу (необязательно)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4897d2] focus:border-transparent resize-none"
            placeholder="Дополнительная информация о заказе..."
          />
        </div>

        {/* Important Information */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">Важная информация:</h4>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Оплата производится по договорённости с тренером</li>
            <li>• Заказ будет обработан только после согласования с тренером</li>
            <li>• Мы свяжемся с вами для подтверждения деталей заказа</li>
          </ul>
        </div>

        {/* Agreements */}
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={paymentAgreed}
              onChange={(e) => setPaymentAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 text-[#4897d2] border-gray-300 rounded focus:ring-[#4897d2]"
            />
            <span className="text-sm text-gray-700">
              Я согласен(а) на оплату заказа по договорённости с тренером
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={dataProcessingAgreed}
              onChange={(e) => setDataProcessingAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 text-[#4897d2] border-gray-300 rounded focus:ring-[#4897d2]"
            />
            <span className="text-sm text-gray-700">
              Я согласен(а) на обработку персональных данных
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={coachAgreementAgreed}
              onChange={(e) => setCoachAgreementAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 text-[#4897d2] border-gray-300 rounded focus:ring-[#4897d2]"
            />
            <span className="text-sm text-gray-700">
              Я понимаю, что заказ будет обработан только после согласования с тренером
            </span>
          </label>
        </div>
      </div>

      {/* Submit Button */}
      <div className="p-6 border-t border-gray-200 bg-white rounded-b-2xl">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !paymentAgreed || !dataProcessingAgreed || !coachAgreementAgreed}
          className="w-full bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Оформление...</span>
            </>
          ) : (
            'Подтвердить заказ'
          )}
        </button>
        {(!paymentAgreed || !dataProcessingAgreed || !coachAgreementAgreed) && (
          <p className="mt-2 text-xs text-center text-red-600">
            Необходимо подтвердить все согласия для оформления заказа
          </p>
        )}
      </div>
    </div>
  );
};

export default Checkout;
